import type { FieldKind } from '@/api/docfiller-types'

/**
 * AI-suggested actions live on a strict review track: every action is
 * `pending` until the user explicitly approves it. The legacy chat SSE
 * still emits `applied=true` field proposals (the backend writes them
 * straight to `current_value`), but those are surfaced here as a separate
 * concern — the safety boundary in this module is that nothing leaves
 * `pending` without an explicit user click on this surface.
 */
export type AiActionStatus =
  | 'pending'
  | 'applying'
  | 'applied'
  | 'rejected'
  | 'failed'
  | 'denied'
  | 'conflict'

/**
 * Provenance for a suggestion — runId / confidence / reasoning when
 * available. Surfaced inline on each action card so the user can judge
 * whether to trust the suggestion before clicking Apply.
 */
export interface AiActionContext {
  runId?: string | null
  confidence?: number | null
  reasoning?: string | null
  /** Free-form label for where the suggestion came from. */
  source?: string
}

interface AiActionBase {
  /** Stable id for this suggestion within the tray (never the DB row id). */
  id: string
  context: AiActionContext
}

/**
 * A single AI-suggested change to a document field. Carries the
 * `before` value at the moment the suggestion was captured so conflict
 * detection can compare to the field's current value at apply time.
 */
export interface FieldEditAction extends AiActionBase {
  kind: 'field-edit'
  documentUuid: string
  fieldUuid: string
  fieldDisplayName: string
  /** Raw XFA / AcroForm name — debug only, never the headline. */
  fieldRawName: string
  fieldKind: FieldKind | null
  before: string | null
  after: string
}

/**
 * Suggested change to project metadata (name + description). The diff
 * sub-object only carries the keys that actually change so the card can
 * render N rows of before/after without the caller having to compute
 * which fields are different.
 */
export interface ProjectMetadataAction extends AiActionBase {
  kind: 'project-metadata'
  workspaceUuid: string
  projectUuid: string
  projectName: string
  changes: {
    name?: { before: string; after: string }
    description?: { before: string | null; after: string | null }
  }
}

/**
 * Suggested project task / checklist item. Distinct from a metadata
 * change because there is no `before` — it's a create-or-skip decision.
 */
export interface ProjectTaskAction extends AiActionBase {
  kind: 'project-task'
  workspaceUuid: string
  projectUuid: string
  taskTitle: string
  taskDescription: string | null
}

export type AiSuggestionAction =
  | FieldEditAction
  | ProjectMetadataAction
  | ProjectTaskAction

/**
 * Result of applying or rejecting a single action. Thrown errors are
 * caught and mapped into `failed`/`denied`/`conflict` statuses by the
 * tray, but callers can also signal a soft conflict via this typed
 * exception when the backend reports the target value drifted.
 */
export class AiActionConflictError extends Error {
  readonly kind = 'conflict'
  readonly currentValue: string | null
  constructor(currentValue: string | null, message?: string) {
    super(message ?? 'Value changed since this suggestion was made.')
    this.name = 'AiActionConflictError'
    this.currentValue = currentValue
  }
}

export class AiActionPermissionError extends Error {
  readonly kind = 'denied'
  constructor(message?: string) {
    super(message ?? 'You do not have permission to apply this change.')
    this.name = 'AiActionPermissionError'
  }
}

/**
 * Hook into the apply / reject pipeline. The tray invokes one of these
 * per action; throwing `AiActionConflictError` or
 * `AiActionPermissionError` produces the matching status. Any other
 * error becomes a generic `failed`.
 *
 * `onForceApply` is invoked when the user clicks "Apply anyway" on a
 * conflicted card. It MUST perform the same write as `onApply` but skip
 * the conflict assertion — the user has acknowledged the value drift.
 * When omitted, the conflict button falls back to `onApply`, which only
 * makes sense for callers that do not gate apply on a conflict check.
 */
export interface AiActionHandlers {
  onApply(action: AiSuggestionAction): Promise<void>
  onReject(action: AiSuggestionAction): Promise<void>
  onForceApply?(action: AiSuggestionAction): Promise<void>
}

/**
 * Per-action UI state — kept by the tray so the caller doesn't have to
 * wire up local state per card. `conflictCurrent` carries the latest
 * known value of the target when the apply collided with a fresh edit,
 * so the conflict UI can show "value is now X" instead of just a
 * generic warning.
 */
export interface AiActionState {
  status: AiActionStatus
  message?: string
  conflictCurrent?: string | null
}

export function isFieldEdit(
  action: AiSuggestionAction,
): action is FieldEditAction {
  return action.kind === 'field-edit'
}

export function isProjectMetadata(
  action: AiSuggestionAction,
): action is ProjectMetadataAction {
  return action.kind === 'project-metadata'
}

export function isProjectTask(
  action: AiSuggestionAction,
): action is ProjectTaskAction {
  return action.kind === 'project-task'
}

/**
 * Stable, human-readable headline for any action. Used by the tray's
 * batch progress text and by tests.
 */
export function actionHeadline(action: AiSuggestionAction): string {
  switch (action.kind) {
    case 'field-edit':
      return action.fieldDisplayName
    case 'project-metadata':
      return action.projectName
    case 'project-task':
      return action.taskTitle
  }
}
